import type { ICreateApiVersionCmd } from "@/data/commands/system/CreateApiVersionCmd";
import type { ICreateOrUpdateTranslationCmd } from "@/data/commands/system/CreateOrUpdateTranslationCmd";
import type { ICreateRecurringTaskCmd } from "@/data/commands/system/CreateRecurringTaskCmd";
import type { IRecurringTask, ITaskExecution, ITaskHandler } from "@/data/model/RecurringTask";
import type { IConfigSchemaRow } from "@/data/model/Shared";
import type {
    IApiVersion,
    IFileInfo,
    IServerUsageTick,
    ITransactionCompareDifference,
    ITransactionCompareResult,
    ITranslation,
} from "@/data/model/System";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import type { IApiVersionsQry } from "@/data/queries/system/ApiVersionsQry";
import type { IRecurringTasksQry } from "@/data/queries/system/RecurringTasksQry";
import type { ITaskHandlerQry } from "@/data/queries/system/TaskHandlerQry";
import type { ITransactionCompareQry } from "@/data/queries/system/TransactionCompareQry";
import type { ITranslationsQry } from "@/data/queries/system/TranslationsQry";
import { DateTime } from "@/data/util/DateTime";

import {
    BaseServiceDao,
    type IDataSource,
    type IEditableDataSource,
    type IPaginatedResult,
    parseCollection,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class SystemDao extends BaseServiceDao {
    public static className = "SystemDao";

    constructor() {
        super("system");
    }

    // tasks
    public async getRecurringTask(id: string): Promise<IRecurringTask> {
        return this.get("task/" + id, undefined, SystemDao.parseRecurringTask);
    }

    public async getRecurringTasks(
        qry: IRecurringTasksQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IRecurringTask>> {
        return this.get("task/", qry, (f) => parsePaginatedResult(f, SystemDao.parseRecurringTask), abortSignal);
    }

    public async createRecurringTask(cmd: ICreateRecurringTaskCmd): Promise<IRecurringTask> {
        return this.post("task/", cmd, SystemDao.parseRecurringTask);
    }

    public async updateRecurringTask(id: string, cmd: ICreateRecurringTaskCmd): Promise<IRecurringTask> {
        return this.put("task/" + id, cmd, SystemDao.parseRecurringTask);
    }

    public async removeRecurringTask(id: string): Promise<void> {
        return this.delete("task/" + id);
    }

    public async executeRecurringTask(id: string): Promise<void> {
        return this.post("task/" + id + "/execute");
    }

    public async getTaskExecutions(
        id: string,
        qry: IPaginatedQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<ITaskExecution>> {
        return this.get(
            "task/" + id + "/execution",
            qry,
            (f) => parsePaginatedResult(f, SystemDao.parseTaskExecution),
            abortSignal
        );
    }

    public async getTaskHandlers(qry: ITaskHandlerQry): Promise<Array<ITaskHandler>> {
        return this.get("task/handler", qry, (f) => parseCollection(f, SystemDao.parseTaskHandler));
    }

    //translation
    public async getTranslations(
        qry: ITranslationsQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<ITranslation>> {
        return this.get("translation/", qry, (f) => parsePaginatedResult(f, SystemDao.parseTranslation), abortSignal);
    }

    public async createOrUpdateTranslation(cmd: ICreateOrUpdateTranslationCmd): Promise<ITranslation> {
        return this.post("translation/", cmd, SystemDao.parseTranslation);
    }

    public async deleteTranslation(locationId: string, translationId: string): Promise<void> {
        return this.delete("translation/" + translationId, { locationId: locationId });
    }

    // util
    public async compareTransactions(qry: ITransactionCompareQry): Promise<ITransactionCompareResult> {
        return this.post("util/comparetransactions", qry, SystemDao.parseTransactionCompareResult);
    }

    // api version

    public async getApiVersion(id: string): Promise<IApiVersion> {
        return this.get("apiversion/" + id, undefined, SystemDao.parseApiVersion);
    }

    public async getApiVersions(
        qry: IApiVersionsQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IApiVersion>> {
        return this.get("apiversion/", qry, (f) => parsePaginatedResult(f, SystemDao.parseApiVersion), abortSignal);
    }

    public async createApiVersion(cmd: ICreateApiVersionCmd): Promise<IApiVersion> {
        return this.post("apiversion/", cmd, SystemDao.parseApiVersion, true);
    }

    public async updateApiVersion(id: string, cmd: ICreateApiVersionCmd): Promise<IApiVersion> {
        return this.put("apiversion/" + id, cmd, SystemDao.parseApiVersion, true);
    }

    public async removeApiVersion(id: string): Promise<void> {
        return this.delete("apiversion/" + id);
    }

    public taskDataSource: IEditableDataSource<IRecurringTask> = {
        getData: (filter, abortSignal) => this.getRecurringTasks(filter as any, abortSignal),
        getSingle: (a) => this.getRecurringTask(a),
        create: (a) => this.createRecurringTask(a),
        update: (a, b) => this.updateRecurringTask(a, b),
        remove: (a) => this.removeRecurringTask(a),
    };

    public apiVersionDataSource: IEditableDataSource<IApiVersion> = {
        getData: (filter, abortSignal) => this.getApiVersions(filter as any, abortSignal),
        getSingle: (a) => this.getApiVersion(a),
        create: (a) => this.createApiVersion(a),
        update: (a, b) => this.updateApiVersion(a, b),
        remove: (a) => this.removeApiVersion(a),
    };

    public getTaskExecutionDataSource(taskId: string): IDataSource<ITaskExecution> {
        return {
            getData: (filter, abortSignal) => this.getTaskExecutions(taskId, filter, abortSignal),
        };
    }

    public getTranslationDataSource(locationId: string): IDataSource<ITranslation> {
        return {
            getData: (filter, abortSignal) => this.getTranslations({ ...(filter as any), locationId }, abortSignal),
        };
    }

    public static parseServerUsageTick(literal: any): IServerUsageTick {
        return {
            errors: parseInt(literal.errors),
            freeMemoryMb: parseInt(literal.freeMemoryMb),
            requests: parseInt(literal.requests),
            requestTimeS: parseFloat(literal.requestTimeS),
            time: DateTime.parseIsoDate(literal.time),
        };
    }

    public static parseRecurringTask(literal: any): IRecurringTask {
        return {
            id: literal.id,
            config: literal.config,
            handler: literal.handler,
            interval: literal.interval,
            isActive: literal.isActive,
            name: literal.name,
            state: parseInt(literal.state),
            nextExecution: literal.nextExecution != null ? DateTime.parseIsoDate(literal.nextExecution) : undefined,
        };
    }

    public static parseTaskExecution(literal: any): ITaskExecution {
        return {
            id: literal.id,
            state: parseInt(literal.state),
            message: literal.message,
            start: DateTime.parseIsoDate(literal.start),
            end: DateTime.parseIsoDate(literal.end),
        };
    }

    public static parseTaskHandler(literal: any): ITaskHandler {
        return {
            name: literal.name,
            handler: literal.handler,
            configSchema: parseCollection(literal.configSchema, SystemDao.parseSchemaConfigRow),
        };
    }

    public static parseSchemaConfigRow(f: any): IConfigSchemaRow {
        return {
            name: f.name,
            dataType: f.dataType,
            placeHolder: f.placeHolder == null ? undefined : f.placeHolder,
            description: f.description == null ? undefined : f.description,
            key: f.key,
            isDisabled: f.isDisabled,
            isRequired: f.isRequired,
            defaultValue: f.defaultValue != null ? f.defaultValue : undefined,
            options:
                f.options != null
                    ? parseCollection(f.options, (a) => {
                          return { key: a.key, value: a.value };
                      })
                    : undefined,
            optionDataSource: f.optionDataSource == null ? undefined : f.optionDataSource,
            unavailableMsg: f.unavailableMsg !== null ? f.unavailableMsg : undefined,
        };
    }

    public static parseTranslation(literal: any): ITranslation {
        return {
            id: literal.id,
            key: literal.key,
            valueDe: literal.valueDe,
            valueEn: literal.valueEn,
            valueFr: literal.valueFr,
            valueIt: literal.valueIt,
        };
    }

    public static parseApiVersion(literal: any): IApiVersion {
        return {
            id: literal.id,
            name: literal.name,
            version: literal.version,
            package: literal.package == null ? undefined : SystemDao.parseFileInfo(literal.package),
        };
    }

    public static parseFileInfo(literal: any): IFileInfo {
        return {
            name: literal.name,
            size: parseInt(literal.size),
            path: literal.path,
        };
    }

    public static parseTransactionCompareResult(literal: any): ITransactionCompareResult {
        return {
            totalCompared: parseInt(literal.totalCompared),
            matched: parseInt(literal.matched),
            rangeFrom: literal.rangeFrom == null ? undefined : DateTime.parseIsoDate(literal.rangeFrom),
            rangeTo: literal.rangeTo == null ? undefined : DateTime.parseIsoDate(literal.rangeTo),
            differences: parseCollection(literal.differences, SystemDao.parseTransactionCompareDifference),
        };
    }

    public static parseTransactionCompareDifference(literal: any): ITransactionCompareDifference {
        return {
            paymentProvider: parseInt(literal.paymentProvider),
            providerAmount: literal.providerAmount == null ? undefined : parseFloat(literal.providerAmount),
            orderId: literal.orderId == null ? undefined : literal.orderId,
            orderNumber: literal.orderNumber == null ? undefined : literal.orderNumber,
            paymentId: literal.paymentId == null ? undefined : literal.paymentId,
            providerId: literal.providerId == null ? undefined : literal.providerId,
            tastierAmount: literal.tastierAmount == null ? undefined : parseFloat(literal.tastierAmount),
            comment: literal.comment == null ? undefined : literal.comment,
            date: literal.date == null ? undefined : DateTime.parseIsoDate(literal.date),
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
        };
    }
}
